/**
 * Checkout resolution — the rule that maps one catalog row (a Registration on
 * an Environment) to the Checkout it names.
 *
 * The Registration decides first; a directory path only breaks a tie among
 * Checkouts that carry none (FOR-36362, FOR-36358). This is the single place
 * that rule lives. Two surfaces need it:
 *
 *  - `resolveCheckoutRefsForRows` resolves every row in a snapshot in one
 *    pass, so `CloudProjectCatalogRow.resolvedRef` carries a real value
 *    instead of the `null` every wire mapper writes
 *    (`cmmdCloudProjectRowMapping.ts`, `cmmdForgeRuntimeListCache.ts`). The
 *    resource that builds the snapshot cannot call this itself — it has no
 *    access to the zustand store that holds Checkout residents — so it runs
 *    in `useCmmdForgeRuntimeListSnapshot`, the hook every reader shares, and
 *    the result is published on the row exactly once per snapshot rather
 *    than re-derived at each of the picker/rendering call sites downstream.
 *  - `resolveCloudRowProjectRefForIdentity` is the identity-on-connect check
 *    `NewThreadLanding.cloud.ts` polls while a Checkout is still
 *    materializing, before it has a catalog row to resolve.
 *
 * A resolved ref feeds rendering and picker questions only
 * (`components/projects/pickableProject.ts`). It must never reach
 * `CloudRemovalTarget.locate()` — removal keys off the Registration id
 * directly, not a derived ref.
 */
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import {
  EnvironmentId,
  type CmmdRegistration,
  type ProjectId,
  type RepositoryIdentity,
  type ScopedProjectRef,
} from "@cmmd-forge/contracts";

import {
  cloudRowWorkspaceDirectoryName,
  directoryNameOf,
  namedWorkspaceRoot,
  normalizePath,
  type CloudEnvironmentProjectSlice,
  type CloudRowProjectIdentity,
} from "../components/sidebar/cloudRuntimeRowState.logic";
import type { CloudProjectCatalogRow } from "./cmmdForgeRuntimeListTypes";

export type { CloudEnvironmentProjectSlice, CloudRowProjectIdentity };

/**
 * Does this resident project's git remote contradict the row's repository?
 *
 * Only ever used to REJECT a directory match. A project whose identity has not
 * been resolved yet reports nothing, and "nothing" is not a contradiction.
 */
function contradictsRowRepository(
  project: { readonly repositoryIdentity?: RepositoryIdentity | null },
  githubRepoFullName: string | null,
): boolean {
  if (!githubRepoFullName) return false;
  const canonicalKey = project.repositoryIdentity?.canonicalKey;
  if (canonicalKey === undefined) return false;
  return !canonicalKey.toLowerCase().endsWith(`/${githubRepoFullName.toLowerCase()}`);
}

/** Does this resident Checkout carry the row's Registration? */
function carriesRegistration(
  registration: CmmdRegistration | null | undefined,
  identity: CloudRowProjectIdentity,
): boolean {
  if (!registration) return false;
  return (
    registration.cmmdOrganizationId === identity.cmmdOrganizationId &&
    registration.cmmdProjectId === identity.cmmdProjectId
  );
}

/**
 * The residents whose directory the row names, for Checkouts that carry no
 * Registration.
 *
 * A resident that DOES carry one has already answered, and its answer was no.
 * Letting it match here would hand a row the Checkout of a different
 * Registration on nothing better than a shared directory name.
 */
function residentsByDirectory<
  T extends { readonly cwd?: string; readonly cmmdRegistration?: CmmdRegistration | null },
>(residents: readonly T[], identity: CloudRowProjectIdentity): readonly T[] {
  const unstamped = residents.filter((project) => !project.cmmdRegistration);
  const seeded = namedWorkspaceRoot(identity.workspaceRoot);
  const bySeededDirectory = seeded
    ? unstamped.filter((project) => normalizePath(project.cwd) === seeded)
    : [];
  if (bySeededDirectory.length > 0) return bySeededDirectory;
  // Case-insensitive: GitHub repository names are, and the directory a Sprite
  // seeds takes its case from CMMD (CMMD-Center/CMMD lives at /projects/cmmd).
  const conventional = cloudRowWorkspaceDirectoryName(identity).toLowerCase();
  return unstamped.filter(
    (project) => directoryNameOf(project.cwd)?.toLowerCase() === conventional,
  );
}

/**
 * Derive a ScopedProjectRef for the project a cloud catalog row NAMES, once
 * its VM has connected.
 *
 * When the cloud Environment connects, the store gains an entry keyed by the
 * Environment ID that the connection registered. A Forge Cloud Environment is
 * per owner, so that entry holds every project the owner has registered and the
 * row has to say which one is its own.
 *
 * FOR-36358: this used to return the environment's FIRST project, which is
 * registration order. On a runtime holding forge, forge-e2e-sandbox and CMMD,
 * every row resolved to forge and every turn ran in the wrong checkout while
 * reporting success.
 *
 * The Registration decides among the residents (FOR-36362). A Checkout created
 * from a Registration carries it on its record, so the row and the project
 * agree on the same durable identity and no path is involved. That is what
 * makes this work on the shared owner Environment, where every connected
 * project reports `cwd: ""` and there is no directory to compare.
 *
 * One filter runs BEFORE it, and the order matters: `contradictsRowRepository`
 * drops any resident whose git remote names a different repository than the
 * row. A stamped Checkout whose remote no longer ends with the row's repository
 * is therefore dropped before its Registration is ever read, and this returns
 * null. That is deliberate for a resident that reports the wrong remote, but it
 * does mean the repository check outranks the identity, not the other way
 * round. A Checkout whose remote is simply unresolved reports nothing, and
 * nothing is not a contradiction, so it survives to the Registration match.
 *
 * When the resident project carries no Registration its directory breaks the
 * tie, and can come from either of two places, so both are read:
 *   1. CMMD's bootstrap seeds `<projectsRoot>/<slug of the project title>` and
 *      stores it as the row's `workspaceRoot`. Preferred, because it is the
 *      directory itself rather than a rule for deriving one.
 *   2. Forge materializes `<root>/<repo name>` or `<root>/<slug of the name>`
 *      through the two routes `cloudRowWorkspaceDirectoryName` mirrors.
 *
 * Returns null when no resident project matches, or when two do. The caller
 * then asks the runtime itself (clone/create is idempotent for a repo row and
 * resolves by `cwd`), which is a round trip rather than a wrong answer.
 */
export function resolveCloudRowProjectRefForIdentity(
  environmentStateById: Readonly<Record<string, CloudEnvironmentProjectSlice | undefined>>,
  environmentId: EnvironmentId,
  identity: CloudRowProjectIdentity,
): { readonly environmentId: EnvironmentId; readonly projectId: ProjectId } | null {
  const envState = environmentStateById[environmentId];
  if (!envState) return null;

  const residents = envState.projectIds.flatMap((projectId) => {
    const project = envState.projectById[projectId as ProjectId];
    if (!project) return [];
    if (contradictsRowRepository(project, identity.githubRepoFullName)) return [];
    return [project];
  });

  const byRegistration = residents.filter((project) =>
    carriesRegistration(project.cmmdRegistration, identity),
  );
  const matches =
    byRegistration.length > 0 ? byRegistration : residentsByDirectory(residents, identity);

  const only = matches.length === 1 ? matches[0] : undefined;
  return only ? { environmentId, projectId: only.id as ProjectId } : null;
}

/** The identity fields a catalog row itself carries, for its own resolution. */
function rowIdentity(row: CloudProjectCatalogRow): CloudRowProjectIdentity {
  return {
    cmmdOrganizationId: row.cmmdOrganizationId,
    cmmdProjectId: row.cmmdProjectId,
    githubRepoFullName: row.githubRepoFullName,
    projectName: row.projectName,
    workspaceRoot: row.workspaceRoot,
  };
}

/**
 * The Checkout ONE catalog row names, or null when nothing resolves.
 *
 * A row with no `runtimeId` has no Environment to resolve against — its
 * Registration has never been provisioned — so it is null without a lookup.
 */
export function resolveCheckoutRefForRow(
  row: CloudProjectCatalogRow,
  environmentStateById: Readonly<Record<string, CloudEnvironmentProjectSlice | undefined>>,
): ScopedProjectRef | null {
  const environmentId = row.cmmdEnvironmentId ?? row.environmentId;
  if (row.runtimeId === null || environmentId === null) return null;
  const resolved = resolveCloudRowProjectRefForIdentity(
    environmentStateById,
    EnvironmentId.make(environmentId),
    rowIdentity(row),
  );
  return resolved ? scopeProjectRef(resolved.environmentId, resolved.projectId) : null;
}

/** Do two refs (or nulls) name the same scoped project? */
function sameResolvedRef(a: ScopedProjectRef | null, b: ScopedProjectRef | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return a.environmentId === b.environmentId && a.projectId === b.projectId;
}

/**
 * Checkout resolution over a whole snapshot: every row's `resolvedRef` is
 * populated in one pass, so no downstream reader re-derives it.
 *
 * Rows a resolution does not change keep their original reference, so a
 * memoized reader (`useMemo`, `useSyncExternalStore`) does not see a new
 * array unless a row's resolution actually moved.
 *
 * `resolveCheckoutRefForRow` returns a fresh `ScopedProjectRef` object on
 * every call, so a same-value resolution must be compared by field, not by
 * identity — the object identity check that skips a row's own re-derivation
 * would otherwise fail on every store change a resolved row survives.
 */
export function resolveCheckoutRefsForRows(
  rows: readonly CloudProjectCatalogRow[],
  environmentStateById: Readonly<Record<string, CloudEnvironmentProjectSlice | undefined>>,
): readonly CloudProjectCatalogRow[] {
  let changed = false;
  const resolved = rows.map((row) => {
    const resolvedRef = resolveCheckoutRefForRow(row, environmentStateById);
    if (sameResolvedRef(resolvedRef, row.resolvedRef)) return row;
    changed = true;
    return { ...row, resolvedRef };
  });
  return changed ? resolved : rows;
}
